import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { memo } from 'react'
import { CloudProvider, useCloudContext } from './contexts/CloudProvider.jsx'

// Import Bootstrap and custom styles
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap-icons/font/bootstrap-icons.css'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import '@express/index.css' // Import express app styles for consistency
import './styles/enhanced-ui.css' // Import enhanced Revatek Connect styles

// Cloud app components - lazy imports for better code splitting
import Layout from './components/Layout.jsx'
import LoginPage from './pages/LoginPage.jsx'
import EmailVerificationPage from './pages/EmailVerificationPage.jsx'
import NotificationSettings from './pages/NotificationSettings.jsx'
import HelpSupport from './pages/HelpSupport.jsx'
import HomePage from './pages/HomePage.jsx'
import Dashboard from './pages/Dashboard.jsx'
import Analysis from './pages/Analysis.jsx'
import Configuration from './pages/Configuration.jsx'
import DeviceInformation from './pages/DeviceInformation.jsx'
import AccessManagement from './pages/AccessManagement.jsx'
import FleetOverview from './pages/FleetOverview.jsx'
import DevicePairingPage from './pages/DevicePairingPage.jsx'
import AcceptInvitationPage from './pages/AcceptInvitationPage.jsx'
import DevicePage from './pages/DevicePage.jsx'
import DeviceAccessRequestsPage from './pages/DeviceAccessRequestsPage.jsx'
import AdminPage from './pages/AdminPage.jsx'
import UserProfile from './components/UserProfile.jsx'
import LoadingSpinner from './components/LoadingSpinner.jsx'

// React Query client with v11+ optimizations
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 30_000, // ES2022+ numeric separators
      gcTime: 5 * 60_000,
    },
  },
})

// Make query client globally available for configuration real-time updates
window.queryClient = queryClient

/**
 * Protected Route wrapper that requires authentication and email verification
 * Optimized for React 19+ with minimal re-renders
 */
const ProtectedRoute = memo(({ children }) => {
  const { user, isAuthenticated, authLoading } = useCloudContext()
  
  if (authLoading) {
    return <LoadingSpinner message="Authenticating..." />
  }
  
  if (!isAuthenticated) {
    // Store the current URL for redirect after login
    const currentUrl = window.location.pathname + window.location.search
    const loginUrl = `/login?returnUrl=${encodeURIComponent(currentUrl)}`
    return <Navigate to={loginUrl} replace />
  }
  
  // Check for email verification (strict verification)
  if (user && !user.emailVerified) {
    return <EmailVerificationPage />
  }
  
  return children
})
ProtectedRoute.displayName = 'ProtectedRoute'

/**
 * App routes component - optimized for React 19+ with memoization
 */
const AppRoutes = memo(() => {
  const { user, authLoading } = useCloudContext()
  
  if (authLoading) {
    return <LoadingSpinner message="Loading Revatek Connect..." />
  }
  
  return (
    <Router>
      <Routes>
        {/* Login route - conditional rendering based on authentication */}
        <Route 
          path="/login" 
          element={
            user ? (
              // User is authenticated, redirect appropriately
              (() => {
                // Only redirect if we're actually on the login page
                // If LoginPage already navigated away, don't interfere
                if (window.location.pathname !== '/login') {
                  return null
                }
                
                const searchParams = new URLSearchParams(window.location.search)
                const returnUrl = searchParams.get('returnUrl')
                
                if (returnUrl) {
                  return <Navigate to={returnUrl} replace />
                }
                
                return <Navigate to="/dashboard" replace />
              })()
            ) : (
              // User is not authenticated, show login page
              <LoginPage />
            )
          }
        />        {/* Email landing page routes - at top level to avoid protection conflicts */}
        <Route path="invitations/:invitationId" element={
          <ProtectedRoute>
            <AcceptInvitationPage />
          </ProtectedRoute>
        } />
        <Route path="devices/:deviceId" element={
          <ProtectedRoute>
            <DevicePage />
          </ProtectedRoute>
        } />
        <Route path="devices/:deviceId/access-requests" element={
          <ProtectedRoute>
            <DeviceAccessRequestsPage />
          </ProtectedRoute>
        } />
        <Route path="admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />
        
        {/* Protected routes */}
        <Route 
          path="/*" 
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  {/* Default route - HomePage handles all user states */}
                  <Route path="/" element={<HomePage />} />
                  
                  {/* Device-specific routes */}
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="analysis" element={<Analysis />} />
                  <Route path="configuration" element={<Configuration />} />
                  <Route path="device-settings" element={<DeviceInformation />} />
                  <Route path="access-management" element={<AccessManagement />} />
                  
                  {/* Device pairing route */}
                  <Route path="add-device" element={<DevicePairingPage />} />
                  
                  {/* Account management routes - unified */}
                  <Route 
                    path="account" 
                    element={
                      <UserProfile 
                        isOpen={true} 
                        onClose={() => {
                          // For route access, navigate to dashboard when closed
                          window.location.href = '/dashboard'
                        }} 
                      />
                    } 
                  />
                  <Route 
                    path="profile" 
                    element={
                      <UserProfile 
                        isOpen={true} 
                        onClose={() => {
                          // For route access, navigate to dashboard when closed
                          window.location.href = '/dashboard'
                        }} 
                      />
                    } 
                  />
                  <Route 
                    path="preferences" 
                    element={
                      <NotificationSettings 
                        isOpen={true} 
                        onClose={() => {
                          // For route access, navigate to dashboard when closed
                          window.location.href = '/dashboard'
                        }} 
                      />
                    } 
                  />
                  <Route 
                    path="help" 
                    element={
                      <HelpSupport 
                        isOpen={true} 
                        onClose={() => {
                          // For route access, navigate to dashboard when closed
                          window.location.href = '/dashboard'
                        }} 
                      />
                    } 
                  />
                  
                  {/* Fleet routes */}
                  <Route path="fleet" element={<FleetOverview />} />
                  
                  {/* Catch-all redirect */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  )
})
AppRoutes.displayName = 'AppRoutes'

/**
 * Main App component - React 19+ optimized with minimal re-renders
 * Provides QueryClient and CloudProvider context to the entire app
 */
const App = memo(() => (
  <QueryClientProvider client={queryClient}>
    <CloudProvider>
      <AppRoutes />
    </CloudProvider>
  </QueryClientProvider>
))
App.displayName = 'App'

export default App
